import { useState } from "react";
import { ChevronDown, Bus, UtensilsCrossed, Sparkles, Sun, BedDouble, MapPin, User } from "lucide-react";

const TYPE_ICON = { travel: Bus, meal: UtensilsCrossed, session: Sparkles, free: Sun, stay: BedDouble };
const TYPE_LABEL = { travel: "Travel", meal: "Meal", session: "Session", free: "Free time", stay: "Stay" };

function groupByDay(blocks) {
  const days = {};
  blocks.forEach((b) => { (days[b.day] = days[b.day] || []).push(b); });
  return Object.keys(days).map(Number).sort((a, b) => a - b).map((d) => ({ day: d, blocks: days[d].sort((x, y) => x.start_time.localeCompare(y.start_time)) }));
}

function dayDate(start, day) {
  if (!start) return "";
  const dt = new Date(`${start}T00:00:00`);
  dt.setDate(dt.getDate() + day - 1);
  return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function Block({ b }) {
  const Icon = TYPE_ICON[b.block_type] || Sparkles;
  return (
    <li className="grid grid-cols-[64px_1fr] gap-4 py-3 sm:grid-cols-[88px_1fr]" data-testid="itinerary-block">
      <div className="pt-0.5 text-sm tabular-nums text-muted-foreground">
        <div className="font-semibold text-foreground">{b.start_time}</div>
        {b.end_time && <div className="text-xs">{b.end_time}</div>}
      </div>
      <div>
        <div className="flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-secondary" strokeWidth={1.6} />
          <div>
            <div className="font-medium text-foreground">{b.title}</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="eyebrow !text-[10px] text-primary/70">{TYPE_LABEL[b.block_type]}</span>
              {b.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{b.location}</span>}
              {b.curator && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{b.curator}</span>}
            </div>
            {b.description && <p className="mt-2 text-sm text-muted-foreground">{b.description}</p>}
          </div>
        </div>
      </div>
    </li>
  );
}

export default function ItineraryTimeline({ blocks, startDate, defaultOpen = "all", highlightDay }) {
  const days = groupByDay(blocks || []);
  const [open, setOpen] = useState(() => new Set(defaultOpen === "all" ? days.map((d) => d.day) : [days[0]?.day]));
  const toggle = (d) => setOpen((s) => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });
  if (!days.length) return <p className="text-sm text-muted-foreground" data-testid="itinerary-empty">No itinerary blocks yet.</p>;
  return (
    <ol className="relative" data-testid="itinerary-timeline">
      <span className="absolute bottom-3 left-[11px] top-3 w-px bg-border" aria-hidden />
      {days.map(({ day, blocks: bl }) => {
        const isOpen = open.has(day);
        return (
          <li key={day} className="relative pb-6 pl-10" data-testid={`itinerary-day-${day}`}>
            <span className={`absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold ${highlightDay === day ? "border-secondary bg-secondary text-white" : "border-primary bg-background text-primary"}`}>{day}</span>
            <button type="button" onClick={() => toggle(day)} className="flex w-full items-center justify-between text-left" data-testid={`itinerary-day-toggle-${day}`}>
              <div>
                <div className="eyebrow text-muted-foreground">{dayDate(startDate, day)}{highlightDay === day ? " · Today" : ""}</div>
                <div className="font-display text-xl tracking-tight text-foreground">Day {day}<span className="text-muted-foreground"> — {bl.length} moments</span></div>
              </div>
              <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && <ul className="rise mt-2 divide-y divide-border/70">{bl.map((b) => <Block key={b.id || `${b.day}-${b.start_time}-${b.title}`} b={b} />)}</ul>}
          </li>
        );
      })}
    </ol>
  );
}
